/**
 * 英語の文言（キーの正）。ここにキーを足すと ja.ts に同じキーが無い限り typecheck が落ちる。
 *
 * 書き方:
 *   - `{name}` = 差し込み（t('key', { name })）。文中に <code>/<Link> を入れるものは tr() で ReactNode を渡す。
 *   - 複数形は { one, other }（count で選ぶ）。
 *   - キー名は「画面.要素」。共通は common.* / error.*。
 *   - コード識別子・CLI コマンド・JSON キーは文言に直接書かず、{code} 等で差し込む（翻訳者が触らない）。
 */
import { enMoney } from './en.money';
import { enFnShare } from './en.fnShare';
import { enFnSecrets } from './en.fnSecrets';
import { enEnterKey } from './en.enterKey';
import { enUpload } from './en.upload';
import { enAccount } from './en.account';
import { enMail } from './en.mail';
import { enAppeals } from './en.appeals';
import { enOAuth } from './en.oauth';
import { enVerify } from './en.verify';
import { enHome } from './en.home';
import { enSlug } from './en.slug';
import { enProjectName } from './en.projectName';
import { enTransfer } from './en.transfer';
import { enMembers } from './en.members';
import { enLoginPasskey } from './en.loginPasskey';
import { enSecretKeys } from './en.secretKeys';
import { enMcpAuth } from './en.mcpAuth';
import { enCliAuth } from './en.cliAuth';
import { enCliDevice } from './en.cliDevice';
import { enOneAccount } from './en.oneAccount';
import { enConnect } from './en.connect';
import { enGuide } from './en.guide';
import { enConfirmAction } from './en.confirmAction';
import { enConfirmPlain } from './en.confirmPlain';
import { enInvite } from './en.invite';
import { enCoreFirst } from './en.coreFirst';
import { enConnectPics } from './en.connectPics';
import { enSelfAds } from './en.selfAds';
import { enPlayEarn } from './en.playEarn';

export const en = {
  // お金まわり（en.money.ts）
  ...enMoney,
  // Functions・X の共有の画面（en.fnShare.ts・2026-09-28 Money の辞書から移した）
  ...enFnShare,
  // Functions の画面の「API キーやパスワード」の欄（en.fnSecrets.ts・2026-10-04 console/secret-input）
  ...enFnSecrets,
  // キーを 1 つ入れるだけの小さな画面（en.enterKey.ts・2026-10-04 creator/chat-secrets）
  ...enEnterKey,
  // ゲームのファイルを入れるだけの小さな画面（en.upload.ts・2026-10-04 creator/web-upload）
  ...enUpload,
  // アカウントの設定（閉鎖）の画面（en.account.ts）
  ...enAccount,
  // メール配信の画面（en.mail.ts）
  ...enMail,
  // BAN の異議申し立ての欄（en.appeals.ts）
  ...enAppeals,
  // Google / Apple / GitHub のログイン・再認証・「ログイン方法」（en.oauth.ts・2026-09-27 creator/dev-oauth-ui）
  ...enOAuth,
  // メールの確認・未確認の案内・パスワードの決め直し（en.verify.ts・2026-09-28 creator/email-unverified）
  ...enVerify,
  // ログイン直後の画面（ホーム・en.home.ts・2026-09-28 creator/home-redesign）
  ...enHome,
  // プロジェクトのスラッグの変更・古いスラッグの転送（en.slug.ts・2026-09-28 creator/slug-rename）
  ...enSlug,
  // プロジェクトの名前の変更（en.projectName.ts・2026-09-28 creator/project-rename・AGT-10）
  ...enProjectName,
  // プロジェクトを人に渡す（en.transfer.ts・2026-10-21 core/handoff-1a）
  ...enTransfer,
  // 共同作業者（en.members.ts・2026-10-22 core/handoff-1b）
  ...enMembers,
  // ログイン用パスキー（en.loginPasskey.ts・2026-09-28 creator/passkey-login）
  ...enLoginPasskey,
  // パスワード再設定の完了・サーバーの鍵の一覧・まとめて作り直す（en.secretKeys.ts・2026-09-28 creator/dashboard-pass）
  ...enSecretKeys,
  // MCP の接続の許可の画面（en.mcpAuth.ts・2026-09-28 creator/mcp-dashboard-login）
  ...enMcpAuth,
  // /cli/authorize（この端末の Kumo CLI を許可・en.cliAuth.ts・2026-09-28 creator/connect）
  ...enCliAuth,
  // /cli/device（デバイスコードでのログイン・en.cliDevice.ts・2026-10-07 console/device-login）
  ...enCliDevice,
  // 1 つの KUMODeck アカウント（ログインを account へ・Player / Creator・招待コード。en.oneAccount.ts・2026-10-07 console/one-account）
  ...enOneAccount,
  // /connect（AI エージェントをつなぐ 1 行をいつでも出す画面・en.connect.ts・2026-10-07 console/connect-again）
  ...enConnect,
  // /guide（使い方ガイド・en.guide.ts・2026-10-07 console/guide-page）
  ...enGuide,
  // /confirm/:id（AI エージェントの本番の変更をリンクで確認・en.confirmAction.ts・2026-09-28 creator/confirm-link）
  ...enConfirmAction,
  // 確認の画面の見える所（見出し・結果・ボタン・安全の 1 行。en.confirmPlain.ts・2026-10-03 console/plain-confirm）
  ...enConfirmPlain,
  // 招待コード（2026-09-29 creator/launch-money）
  ...enInvite,
  // 芯を先に: ナビの見出し・概要の「AI エージェントに頼む」・ホスティングの空の状態・招待クレジットの知らせ（en.coreFirst.ts・2026-09-30 console/core-first）
  ...enCoreFirst,
  // 「AIをつなぐ」の絵（en.connectPics.ts・2026-10-19 design/visual-guides）
  ...enConnectPics,
  // 自社の広告の枠（作り手が自分のゲームの広告を出す・en.selfAds.ts・2026-10-22 money/self-serve-ads-dash）
  ...enSelfAds,
  // 「初日から稼げる」の画面（en.playEarn.ts・2026-10-26 money/earn-build）
  ...enPlayEarn,
  // ------------------------------------------------------------ app / common
  'app.title': '{product} dashboard',
  'app.starting': 'Starting the dashboard',
  'common.loading': 'Loading',
  'common.loadingMore': 'Loading…',
  'common.loadMore': 'Load more',
  'common.cancel': 'Cancel',
  'common.working': 'Working…',
  'common.copy': 'Copy',
  'common.copied': 'Copied ✓',
  'common.close': 'Close',
  'common.retry': 'Try again',
  'common.snippet': 'snippet',
  'common.optional': '(optional)',
  'common.unnamed': 'Unnamed',
  'common.guest': 'Guest',
  'common.never': 'never',
  'common.language': 'Language',
  'common.menu': 'Menu',
  'common.logOut': 'Log out',
  'common.skip': 'Skip to content',
  /** 2 つの文をつなぐ区切り（英語は半角スペース、日本語は句点のあとに詰める） */
  'common.sentenceSep': ' ',
  'common.brandSub': 'dashboard',

  'time.justNow': 'just now',
  'time.dur.s': '{s}s',
  'time.dur.ms': '{m}m {s}s',
  'time.dur.hm': '{h}h {m}m',

  // ------------------------------------------------------------ env
  'env.development': 'Test',
  'env.production': 'Published',
  /** 文中の環境名（"No development users yet"） */
  'env.inline.development': 'your test app',
  'env.inline.production': 'your published app',
  'env.tag.development': 'TEST APP',
  'env.tag.production': 'LIVE APP',
  'env.band.development': 'You are working on your test app ({url}). Nothing you change here affects your published app.',
  'env.band.production': 'You are working on your published app ({url}). Changes here show up for people using it right away.',
  'env.band.development_noUrl': 'You are working on your test app. Nothing you change here affects your published app.',
  'env.band.production_noUrl': 'You are working on your published app. Changes here show up for people using it right away.',
  'env.chip.development': 'test',
  'env.chip.production': 'live',

  // ------------------------------------------------------------ errors
  'error.notAvailableTitle': "{what} isn't available yet",
  'error.notAvailableBody': "This {product} server doesn't support this screen yet. It will show up here on its own once it does — no dashboard update needed.",
  'error.thisPanel': 'This panel',
  'error.connection': 'Connection problem',
  'error.generic': 'Something went wrong',
  'error.email_taken': 'An account with this email already exists. Log in instead.',
  'error.slug_taken': 'That name is already used in another web address. Try another one.',
  // 400 slug_reserved（Core の統合⑰）: www・api・admin・login など運用の名前と Kumo を含む名前は予約語。空いている候補のボタンが下に出る
  'error.slug_reserved': 'That name is not available. Names like www, admin or login, and any name containing "kumo", cannot be used in your web address.',
  'error.invalid_request': 'Invalid request: {detail}',
  'error.unauthorized': 'You were signed out because it has been a while. Please log in again.',
  'error.invalidCredentials': 'Email or password is incorrect.',
  'error.forbidden': 'This account cannot do that.',
  'error.not_found': 'Not found. It may have been deleted.',
  'error.payouts_not_configured': 'Payouts are not set up yet. Finish payout onboarding before taking live payments.',
  'error.payments_not_configured': 'Payments are not configured on this server.',
  'error.payment_provider_error': 'The payment provider returned an error: {detail}',
  'error.already_owned': 'The user already owns this product.',
  'error.not_refundable': 'This purchase cannot be refunded: {detail}',
  'error.rate_limited': 'Too many tries. Try again in {seconds} seconds.',
  'error.network_error': 'Cannot reach {product}. Check your connection and try again.',
  'error.route_missing': 'This feature is not available yet.',
  'error.internal_error': 'Something unexpected went wrong (not because of anything you did). Try again in a moment.',
  'error.http_error': 'That did not work ({detail}). Try again in a moment.',
  // 独自ドメイン（hosting / Functions 共通のコード。apps/server/src/modules/compute/domains.ts）
  // 統合⑰（SEC-R3-07）から 409 domain_taken は「同じ環境の Functions / 配信で使用中」のときだけ。
  // 他のゲーム・他の人が申し込んだ名前は受け付け、TXT で持ち主を証明できた方が取る（= 「別のゲームが使用中」とはもう言わない）
  'error.domain_taken': 'This domain is already used in this project for the other purpose (Functions or your published app). Remove it there first, or use a different name.',
  'error.domain_limit': 'You already have {limit} custom domains waiting for verification. Add their TXT records (verified domains do not count) or remove ones you no longer need, then try again.',
  'error.custom_domain_capacity': 'No more custom domains can be added on this {product} right now. Try again later.',
  'error.custom_domains_unavailable': 'Custom domains are not set up on this {product} server.',
  'error.production_only': 'Custom domains work only in your published app.',
  // 最初の公開で閉じている部品（サーバーが 403 feature_unavailable・どの鍵でも）。本人は ON にできないので、ON にする方法は書かない
  'error.feature_unavailable': '{feature} is not available.',
  'error.feature_disabled': '{feature} is off for this project. Turn it on in Features on the Overview page, or ask your AI to "turn on {feature}".',
  'error.deployment_not_ready': 'This version is not ready yet (it is still uploading or was not finished), so it cannot be made live. Ask your AI to "publish again".',
  'error.deployment_failed': 'This version failed or expired. Ask your AI to "publish again".',
  'error.functions_disabled': 'Functions are off. Turn them on first (press "Turn on" on the Functions page, or ask your AI).',
  'error.functions_busy': 'Your Functions are being updated right now. Wait a moment and try again.',
  'error.functions_not_deployed': 'Publish your Functions first (ask your AI to "publish the server").',
  'error.durable_object_removed': 'This update would delete some data your Functions stored (the part no longer used). Nothing was changed. Ask your AI to check whether it is OK to delete it.',
  // 2026-09-28 Core: REV-07（公開は環境ごとに 1 本ずつ）・REV-11（環境の資源の種類ごとの上限。古い資源は自動では消えない）
  'error.publish_in_progress': 'Another publish is still being sent. Nothing was changed: wait a moment, then try again.',
  'error.resource_limit': 'You can have at most {limit} {kind} ({existing} now). Old ones are not removed automatically (a new name makes a new one and keeps the old one): reuse the names you have, or remove unused ones, then publish again. Your AI can fix this if you pass this message on.',
  'error.share_images_disabled': 'Share card images are off for this project.',
  'error.share_tags_disabled': 'Share card tags are off for this project.',
  'error.share_inAppBrowser_disabled': 'In-app browser handoff is off for this project.',

  // ------------------------------------------------------------ auth
  // ログイン・登録の画面の左側（AuthPage.tsx AuthLayout）。2026-09-28 creator/plain-words: 決め台詞（"one sky." 等）をやめ、
  // 場所の名前と「ログインすると何ができるか」だけを書く（founder「抽象的な言い回しはやめていいよ」・MASTER-PLAN §1c）
  'auth.sideTitle': '{product} dashboard',
  'auth.sideBody': 'Here you connect your AI agent, add prepaid balance, and check your projects, their users, what they used and what they earned.',
  // 売上（payouts）を見せない間の同じ文（2026-09-29 creator/launch-money）
  'auth.sideBodyLaunch': 'Here you connect your AI agent, add prepaid balance, and check your projects, their users and what they used.',
  'auth.signupTitle': 'Create your account',
  'auth.loginTitle': 'Log in to {product}',
  'auth.signupLead': 'One account for all your projects. Usage is billed at cost from your prepaid balance (no free tier).',
  // 広告から来た人だけ（2026-10-18 gtm/early-fixes・GTM の確認 early-review.md の直す案 6）。WHY: 「$5 もらえる」の直後の「(no free tier)」は冷水。
  //   事実（前払い・原価）は残し、"free" の語を出さない（wording.test.ts）。{amount} = platform/info の額。分からないときは …Credit
  'auth.signupLeadAd': 'One account for all your projects. You pay for what you use, at cost, from a prepaid balance. Your {amount} credit is the first part of it.',
  'auth.signupLeadAdCredit': 'One account for all your projects. You pay for what you use, at cost, from a prepaid balance. Your invite credit is the first part of it.',
  'auth.loginLead': 'Log in to manage your projects.',
  // AI のアプリ（ChatGPT・Claude など）の Connect から /login に来た時だけ、上の 1 行の代わりに出す（2026-10-31 core/login-from-ai・WHY は ../loginFromAi.ts）。
  //   アプリの名前は入れない（ログイン前は読めない・推測で出さない）。見出しに {product} があるので繰り返さない
  'auth.loginLeadAiConnect': 'Log in to connect your AI app.',
  'auth.name': 'Name',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.passwordHint': '{min}+ characters.',
  // 2026-09-30 console/words-pass: 「売上（revenue）」を外した（お金を閉じている間に出る所 = 登録・再設定・ログイン方法の 4 か所で使う。
  //   パスワードを長くする理由の説明なので、出し分けずに開けても閉じても正しい言い方にした）
  'auth.passwordTooShort': "Use at least {min} characters — this account controls your projects, your users' data and what you publish.",
  'auth.busy': 'One moment…',
  // /early の「Continue with GitHub / Google」から来て、プロバイダへ移る間だけ出す 1 行（2026-10-22 site/early-inline-signup・AuthPage の handoff）
  'auth.handoff': 'Opening {provider}…',
  'auth.createAccount': 'Create account',
  'auth.logIn': 'Log in',
  'auth.haveAccount': 'Already have an account? {link}',
  'auth.newHere': 'New to {product}? {link}',
  'auth.createAnAccount': 'Create an account',
  // 2026-09-28 creator/login-redesign: パスワードの表示の切り替え・前回の方法の印・この端末に保存・パスキーでのログイン（置き場）
  'auth.showPassword': 'Show password',
  'auth.hidePassword': 'Hide password',
  'auth.lastUsed': 'Last used',
  'auth.rememberDevice': 'Save email and sign-in method on this device',
  'auth.passkeyLogin': 'Sign in with a passkey',
  // 出金を閉じている間は「出金用のパスキー」を言わない（2026-09-30 console/words-pass・OAuthButtons.tsx が isLaunchHidden('payouts') で出し分け）
  'auth.passkeyFailedLaunch': "This passkey couldn't sign you in. Use a sign-in passkey added under Account → Sign-in methods, or sign in another way.",
  'auth.passkeyFailed': "This passkey couldn't sign you in. Use a sign-in passkey added under Account → Sign-in methods (withdrawal passkeys can't sign in), or sign in another way.",

  // ------------------------------------------------------------ projects
  'projects.eyebrow': 'Your projects',
  'projects.title': 'Projects',
  'projects.new': '+ New project',
  'projects.emptyTitle': 'No projects yet',
  'projects.emptyBody': 'A project is one app or game. It has a published app and a test app for trying things out, each with its own URL, users and keys.',
  'projects.createFirst': 'Create your first project',
  'projects.created': 'Created {ago}',
  'projects.createdEyebrow': 'Project created',
  'projects.savedOpen': "I've saved my keys — open project →",
  'projects.form.title': 'New project',
  'projects.form.name': 'Project name',
  'projects.form.namePlaceholder': 'Booking App',
  'projects.form.slug': 'Name in your web address',
  // 2026-09-28 creator/slug-rename: 以前は「変更不可」。後から変えられるようになった（founder）。間隔は 2026-10-04 に 30 日から 1 日へ（サーバーの SLUG_CHANGE_INTERVAL_DAYS）
  //   （作成の画面はまだサーバーの値を持たないので文言に書く。値が変わったらここと ja を直す）
  'projects.form.slugNote': '(optional, can be changed later: once a day)',
  'projects.form.slugHint': 'The name that goes in your app’s web address: for example, typing “neko-jump” makes it the “neko-jump” part of the address. If you leave it empty, it comes from the project name.',
  'projects.form.slugInvalid': 'The name must be 3–58 characters: lowercase letters, digits and hyphens.',
  'projects.form.slugSuggestions': 'Free right now. Pick one to put it in the field:',
  'projects.form.creating': 'Creating…',
  'projects.form.submit': 'Create project',

  // ------------------------------------------------------------ keys (reveal + page)
  'key.kind.publishable': 'Key in your app (pk_)',
  'key.kind.secret': 'Server-only key (sk_)',
  'key.secretNote': 'With it, anyone can change settings and publish. Never show it to anyone or paste it into a conversation with your AI',
  'key.publishableNote': 'It goes into your app’s code, so people using the app can see it. That is fine for this key',
  'key.secretWarnTitle': 'Server-only keys (starting with sk_) are shown only once.',
  'key.secretWarnBody': '{product} does not keep a copy. Copy them into your password manager now — if you lose one, turn it off and make a new key.',
  'key.quickstart': 'Quickstart',
  'key.quickstartLead': "Paste this into your app's HTML. It uses your test app's key, so nothing you try touches your published app.",
  'key.quickstartComment': 'sign the user in',
  'keys.title': 'Keys (pk_ / sk_)',
  'keys.storedHide': "I've stored it — hide",
  'keys.createTitle': 'Create a key for {env}',
  'keys.kindLabel': 'Key type',
  'keys.label': 'Label',
  'keys.labelSecret': 'Label, e.g. GitHub Actions',
  'keys.labelPublishable': 'Label, e.g. itch.io build',
  'keys.creating': 'Creating…',
  'keys.create': 'Create key',
  'keys.help': '{publishable} go into your app’s code, so people using the app can see them. That is fine: they can only do what a user can. With {secret}, anyone can change settings and publish. Never show them to anyone or paste them into a conversation with your AI.',
  'keys.helpPublishable': 'Keys in your app (starting with pk_)',
  'keys.helpSecret': 'server-only keys (starting with sk_)',
  'keys.listTitle': 'Keys',
  'keys.showRevoked': 'Show {count} turned-off keys',
  'keys.col.key': 'Key',
  'keys.col.type': 'Type',
  'keys.col.created': 'Created',
  'keys.col.lastUsed': 'Last used',
  'keys.col.actions': 'Actions',
  'keys.revokedAgo': 'turned off {ago}',
  'keys.revoke': 'Turn off',
  'keys.revokeTitle': 'Turn off {key}?',
  'keys.revokeTitleGeneric': 'Turn off this key?',
  'keys.revokeConfirm': 'Turn off key',
  'keys.revokeBody': 'This key stops working right away. This cannot be undone.',
  'keys.revokeBodyPublishable': 'This key stops working right away — any copy of your app that has it stops working until you put in a new key and publish again. This cannot be undone.',

  // ------------------------------------------------------------ shell
  'shell.navLabel': 'Project navigation',
  'shell.project': 'Project',
  'shell.environment': 'App you are working on',
  'nav.group.manage': 'Manage',
  'nav.overview': 'Overview',
  'nav.players': 'Users',
  'nav.leaderboards': 'Leaderboards',
  'nav.multiplayer': 'Multiplayer',
  'nav.config': 'Config',
  'nav.hosting': 'Hosting',
  'nav.payments': 'Payments',
  'nav.keys': 'Keys (pk_ / sk_)',
  'nav.audit': 'Activity log',

  // ------------------------------------------------------------ features (2026-09-27 task/toggles)
  'features.title': 'Features',
  'features.count': '{on} of {total} on',
  'features.body': 'Every feature is off until you turn it on. Anything that is off is refused, so nobody can run up your bill on features you do not use. Switch one here, or ask your AI, for example "turn on saves". (The source is your kumo.config.json; to type it yourself: {cmd}, then {push}.)',
  'features.none': 'Nothing is on yet — only guest sign-in and reading the config work.',
  'features.on': 'on',
  'features.off': 'off',
  'features.turnOn': 'Turn on',
  'features.turnOff': 'Turn off',
  'features.setUpSignIn': 'Set up',
  'features.openFunctions': 'Open Functions',
  'features.confirmOnTitle': 'Turn on {name} in {env}?',
  'features.confirmOffTitle': 'Turn off {name} in {env}?',
  'features.confirmOnBody': 'People using {env} see the change within a few seconds.',
  'features.confirmOffBody': 'Within a few seconds, what you turn off can no longer be used in {env}, and the parts of your app that use it stop working.',
  'features.confirmOn': 'Turn on in {env}',
  'features.confirmStop': 'Stop the site',
  'features.stopServing': 'The live site is published here: {url}. Turning off {features} takes it offline right away. Nothing has changed yet: press “Stop the site” to go ahead.',
  'features.stopServingNoUrl': 'The site is live. Turning off {features} takes it offline right away. Nothing has changed yet: press “Stop the site” to go ahead.',
  'features.confirmOff': 'Turn off in {env}',
  'features.alsoOn': 'The others are needed by it, so they are turned on too.',
  'features.alsoOff': 'The others need it, so they are turned off too.',
  'features.localNote': 'Your local {file} is not changed. Unless you make the same change there, the next {push} puts it back.',
  'features.typeSlug': 'This is your published app. Type {slug} to confirm.',
  'features.stale': 'The config changed since this page loaded (now v{version}). Check the list again and try again.',
  'features.doneTitle': 'Done — config v{version} is live.',
  'features.doneSame': 'Nothing changed — it was already like that.',
  'features.pendingTitle': 'Changed here: {names}',
  'features.pendingBody': 'If your local file does not have this yet, the next push will undo it.',
  'features.revertedTitle': 'Put back by a later push: {names}',
  'features.revertedBody': 'You changed these here, but a later push from your local file did not have them.',
  'features.localFix': 'Make the same change in your local {file}:',
  'features.localManual': 'Set {path} to {value} in the file.',
  'features.localManualThen': 'Then run {cmd}.',
  'features.dismiss': 'Done in my file',
  // 画面が使う機能が OFF のときの案内（components/FeatureHint.tsx・dashboard-review 2026-09-27）
  // 機能の表示名（サーバーの features.ts の title は英語のみで、運営側の言葉が混じるため画面用に持つ。無い key はサーバーの title）
  'feature.emailLogin': 'Email sign-in (sign up, verify, password reset)',
  'feature.googleLogin': 'Sign in with Google',
  'feature.discordLogin': 'Sign in with Discord',
  'feature.appleLogin': 'Sign in with Apple',
  'feature.xLogin': 'Sign in with X',
  'feature.saves': 'Cloud saves',
  'feature.stats': 'Stats (send and read user stats)',
  'feature.leaderboards': 'Leaderboards',
  'feature.achievements': 'Achievements and missions',
  'feature.inventory': 'Currencies and items',
  'feature.exchanges': 'In-app shop and exchanges',
  'feature.multiplayer': 'Multiplayer rooms and matchmaking',
  'feature.p2p': 'Peer-to-peer multiplayer (WebRTC; no referee, relayed through TURN by default)',
  // リアルタイムの部屋（features.realtimeChannels・2026-09-27）。「チャット機能」とは書かない（MASTER-PLAN §1c: フラットな土台として出す）
  'feature.realtimeChannels': 'Realtime channels (messages delivered instantly in a named channel; nothing is stored)',
  // 音声通話（features.voice・requires realtimeChannels・2026-09-27 creator/voice-docs）。「ボイチャ機能」とは書かない（MASTER-PLAN §1c）。
  // 「録音しない」を名前に入れる WHY: ON にする人が真っ先に気にするのがプライバシーで、Kumo は録音しない（realtime/README.md「音声通話」）
  // 人数（2026-09-28 creator/voice-sfu-docs）: Core の voice-sfu で 9 人以上は中継サーバー（SFU）・上限 100 になったので「8 人まで・P2P」から直した
  'feature.voice': 'Voice calls in realtime channels (up to 100 people; nothing is recorded)',
  'feature.payments': 'In-app purchases',
  // 2026-10-19 creator/ads-open: 機能の一覧の説明。キーの ads と同じ語の繰り返しにしない（見た目の確認の指摘）。収益の語は出金と一緒に閉じた言葉なので使わない（wordsPass.test.ts）
  'feature.ads': 'Rewarded video ads',
  // 表示の広告のカード（2026-10-19 creator/ads-open・components/DisplayAdsCard.tsx・displayAds.ts）。founder: 100%・Adsterra の確定（数日）と 60 日の確認期間の後に残高・
  // 「表示の回数の比で分ける概算」を正直に書く。現金・出金の言葉は出さない（displayAds.test.ts・wordsPass.test.ts）
  'displayAds.title': 'Banner ads',
  'displayAds.body': '{product} adds the ad to your public page for you, with no code. It appears from the next visit and disappears from the next visit when you turn it off.',
  'displayAds.name.banner': 'Banner',
  'displayAds.name.socialBar': 'Social Bar',
  // 2 つの選択肢の違いが一目で分かる 1〜2 行（2026-10-19 founder「ちゃんとどういう広告なのかは説明する」・tasks/social-bar-explain.md）。
  // Social Bar の事実は examples/adsterra-banner-demo/README.md（画面の上に重なる・形は Adsterra・タブの文字・報酬なし）。
  // 「稼げる」「収益が上がる」と約束しない = 良い点は「なりやすい・決まった額はない・ならないこともある」。決めるのは作り手（脅さない）
  'displayAds.kind.banner': 'A bar that stays at the bottom of the page (320×50 on phones, 728×90 on wider screens). Always the same shape, and quiet.',
  'displayAds.kind.socialBar': 'Ads that appear on top of your app’s screen, in a form Adsterra picks. More noticeable than the banner.',
  // 2 つの広告の見た目の絵（components/DisplayAdsPictures.tsx・2026-10-19 design/visual-guides・founder「文字ばっかり」）。札は短く、事実は上の kind.* と同じ
  'displayAds.pic.label': 'Where each one shows',
  'displayAds.pic.banner': 'Banner: a bar at the bottom',
  'displayAds.pic.socialBar': 'Social Bar: on top of the screen',
  'displayAds.pic.alt.banner': 'A phone with your app on the screen and an ad bar fixed at the bottom',
  'displayAds.pic.alt.socialBar': 'A phone with your app on the screen, a notification-like ad over it, and the browser tab text changed to “(1) New Message!”',
  'displayAds.sb.more': 'What Social Bar is like',
  'displayAds.sb.over': 'It appears on top of your app’s screen, not in a strip of its own.',
  'displayAds.sb.forms': 'Adsterra picks the form each time: a small notification-like ad, a widget, an icon, or an ad that covers the whole screen. You can’t pick the form.',
  'displayAds.sb.title': 'It may change the text on the browser tab, for example to “(1) New Message!”.',
  'displayAds.sb.noReward': 'Users get nothing for seeing it: it is not a rewarded ad, and your app is not told when it shows.',
  'displayAds.sb.goodLabel': 'Good',
  'displayAds.sb.good': 'Because it stands out more, it tends to bring in more than the banner. There is no set amount, and it may not.',
  'displayAds.sb.considerLabel': 'To consider',
  'displayAds.sb.consider': 'It can cover part of your app while people use it, and the tab text can look like a message.',
  'displayAds.sb.yours': 'It’s your call: use it with the banner, on its own, or not at all. You can turn it off any time.',
  'displayAds.onTitle': 'Banner ads are on.',
  'displayAds.offTitle': '{name} is off.',
  'displayAds.share': 'You get 100% of what Adsterra actually pays for the ads on your app.',
  'displayAds.estimate': 'All {product} apps use the same ad slots, so your part is an estimate: each day, the payment for a slot is split by how many times the ad was shown on each app.',
  'displayAds.when': 'It reaches your balance after Adsterra confirms the numbers (a few days) and a 60-day review period.',
  'displayAds.where': 'Ads show only on your public page at {host}: not on the test version or a custom domain, and not when your audience is kids or mixed. Visitors in Europe are asked before ads load.',
  'displayAds.devOnly': 'Ads show only on the public version. Switch to {env} to turn them on.',
  'displayAds.needsHosting': 'Turn on hosting first: the ad is added to the page {product} serves.',
  'displayAds.confirmOnTitle': 'Turn on {name}?',
  'displayAds.confirmOffTitle': 'Turn off {name}?',
  'displayAds.confirmOnBody': 'The ad appears on your public page from the next visit.',
  'displayAds.confirmOffBody': 'The ad disappears from your public page from the next visit.',
  // KUMODeck 自身の広告だけが出る作り手の版（2026-10-27 console/earn-ads-card・displayAds.ts adsCardMode の earn / house）。
  // 告知のメールの「switch on "KUMODeck ads" on its Overview」とカードの名前をそろえる
  'displayAds.title.kumo': '{product} ads',
  'displayAds.onTitle.kumo': '{product} ads are on.',
  'displayAds.house.body': 'Right now, the only ads here are short promos for {product}. {product} adds them to your public page for you, with no code.',
  'displayAds.kind.socialBarHouse': 'Not shown right now: only {product} ads run. You can turn it off here.',
  'feature.mail': 'Email to your users',
  'feature.friends': 'Friends',
  'feature.presence': 'Online status of friends',
  'feature.chat': 'Chat',
  'feature.guilds': 'Guilds',
  'feature.events': 'Events calendar',
  'feature.loginBonuses': 'Login bonuses',
  'feature.gifts': 'Gift box (gifts you send to users)',
  'feature.serials': 'Serial codes',
  'feature.gachas': 'Free gacha',
  'feature.moderation': 'Reports and sanctions',
  'feature.statsVerification': 'Stat checks (value ranges and a check by your own server)',
  'feature.hosting': 'Hosting (publishing your app)',
  'feature.serverRendering': 'Server-rendered apps (pages built on the server, needs Hosting)',
  'feature.customDomains': 'Custom domain for your published app (app.example.com, published app only)',
  'feature.shareImages': 'Share card images',
  'feature.shareTags': 'Share card tags in your hosted page',
  'feature.shareLinks': 'Share links and challenges',
  'feature.shareTracking': 'Visits per shared post',
  'feature.inAppBrowser': 'Open in the real browser from in-app browsers',
  'feature.ai': 'AI from your server (server-only key)',
  'feature.functions': 'Functions (your server code and database)',
  'feature.network': 'User account shared across projects (not in use yet)',
  'featureHint.title': 'Off in {env}: {names}',
  'featureHint.body': 'Nothing shows up here until it is on — your app gets refused for features that are off. Turn it on in your local file and push, or in {link}.',
  'featureHint.link': 'Features on the Overview page',
  // 空の状態の次の一手（CLI / Claude Code の MCP。dashboard-review 2026-09-27）
  'projects.emptyCli': 'Or from your project folder: {cmd}',
  'mp.noRoomsStarter': 'Starting fresh? {cmd} gives you a working two-person game.',
  'keys.emptyTitle': 'No active keys in {env}',
  'keys.emptyBody': 'Your app needs keys. Create one above, or ask your AI to set them up (to type it yourself: {cmd}).',
  'share.turnOnCmd': 'Or turn on X cards, share links and tracking and push, in one step: {cmd}',
  'share.emptyCli': 'Posting on X yourself? Make a tracked link with {cmd} (from an AI agent: {mcp}).',
  'fn.noDeploysCmd': 'Ask your AI to "publish the server", or run {cmd}.',
  'key.quickstartNext': 'turn on what you use first: {cliName} features on saves',
  // ------------------------------------------------------------ overview
  'overview.title': 'Overview',
  'overview.keyNumbers': 'Key numbers',
  'overview.players': 'Users',
  'overview.banned': '{count} banned',
  'overview.allTime': 'all time',
  'overview.active24h': 'Active · 24h',
  'overview.active24hHint': 'used in the last day',
  'overview.new24h': 'New · 24h',
  'overview.new7d': '{count} in 7 days',
  'overview.config': 'Config',
  'overview.pushedAgo': 'pushed {ago}',
  'overview.nothingPushed': 'nothing pushed yet',
  'overview.apiKeys': 'Keys (pk_ / sk_)',
  'overview.revoked': '{count} turned off',
  'overview.active': 'active',
  'overview.dauTitle': 'Daily active users',
  'overview.dauRange': 'Last 14 days · UTC',
  'overview.dauLabel': 'Daily active users, last 14 days',
  'overview.noPlayersTitle': 'No users yet',
  'overview.noPlayersBody': 'Add the SDK to your app and sign in as a guest. Your first user shows up here a few seconds later.',
  'overview.nextSteps': 'Next steps',
  'overview.step2Title': 'Sign in your first user.',
  'overview.step2Body': 'Add the SDK to your app, with the key for your app (starting with pk_) you copied when you created the project (or make one on the Keys page):',
  'overview.step3Title': 'Publish it.',
  'overview.step3Body': '{cmd} publishes it. You can go back to an earlier one at any time from {link}.',
  'overview.step3Link': 'Hosting',

  // ------------------------------------------------------------ chart
  'chart.total': 'Total {value}.',

  // ------------------------------------------------------------ players
  'players.title': 'Users',
  'players.search': 'Search users',
  'players.searchPlaceholder': 'Search by name, email or user ID prefix',
  'players.noMatchTitle': 'No users match “{q}”',
  'players.noMatchBody': 'Search matches the start of a user ID, any part of a display name, or any part of an email.',
  'players.emptyTitle': 'No users in {env} yet',
  'players.emptyBody': 'Users appear here after your app calls {guest} with a {prefix} key (guest sign-in is always on).',
  'players.col.player': 'User',
  'players.col.login': 'Login',
  'players.col.joined': 'Joined',
  'players.col.lastSeen': 'Last seen',
  'players.col.status': 'Status',
  'players.guestPill': 'guest',
  'players.banned': 'banned',
  'players.active': 'active',

  // ------------------------------------------------------------ player drawer
  'player.fallbackTitle': 'User',
  'player.bannedAgo': 'Banned {ago}',
  'player.provider.guest': 'guest',
  'player.provider.email': 'email',
  'player.noReason': 'No reason recorded.',
  'player.id': 'User ID',
  'player.account': 'Account',
  'player.guestAccount': 'Guest (device only)',
  'player.linkedAccount': 'Linked account',
  'player.joined': 'Joined',
  'player.lastSeen': 'Last seen',
  'player.sessions': 'Signed-in devices',
  'player.signIn': 'Sign-in methods',
  'player.stats': 'Stats',
  'player.noStats': 'No stats recorded.',
  'player.inventory': 'Inventory',
  'player.noInventory': 'Nothing owned.',
  'player.achievements': 'Achievements',
  'player.noAchievements': 'None unlocked.',
  'player.unban': 'Unban user',
  'player.ban': 'Ban user',
  'player.thisPlayer': 'this user',
  'player.banTitle': 'Ban {name}?',
  'player.unbanTitle': 'Unban {name}?',
  'player.unbanConfirm': 'Unban',
  'player.banBody':
    'Signs them out on every device in {env} and blocks new logins. Any open multiplayer connection is closed at once (their seat is freed for others). Everything else stops working for them within 15 minutes.',
  'player.reason': 'Reason',
  'player.reasonNote': '(kept in the activity log)',
  'player.reasonPlaceholder': 'e.g. speed hack in multiplayer',
  'player.unbanBody': 'They can sign in again with their existing account. Progress was never deleted.',
  'player.privacy': 'Data & privacy',
  'player.privacyNote': 'For data requests from this user (GDPR, App Store / Google Play account deletion). Both actions are recorded in the activity log.',
  'player.exportData': 'Export data',
  'player.exporting': 'Exporting…',
  'player.exported': 'Saved {file}.',
  'player.delete': 'Delete user',
  'player.deleteTitle': 'Permanently delete {name}?',
  'player.deleteBody':
    'Deletes their account in {env}: sign-in methods (emails), saves, stats and everything else stored for them. They are signed out everywhere and any open multiplayer connection is closed. This cannot be undone — export their data first if they asked for a copy.',
  'player.deletePurchases': 'Purchases are kept for your accounting records, but are no longer linked to anyone.',
  'player.deleteTypeId': 'Type {id} to confirm',
  'player.deleteConfirm': 'Delete forever',
  'player.deleted': 'User deleted.',

  // ------------------------------------------------------------ config
  'config.title': 'Config',
  'config.liveVersion': 'live v{version}',
  'config.neverPushed': 'never pushed',
  'config.download': 'Download {file}',
  'config.nothingPushedTitle': 'Nothing pushed to {env} yet.',
  'config.nothingPushedBody': 'This is a starter config. Edit it here, or keep it in your repo and run {cmd}.',
  'config.unsaved': '● unsaved changes',
  'config.inSync': 'in sync',
  'config.editorLabel': 'Config JSON',
  'config.pushing': 'Pushing…',
  'config.push': 'Validate & push to {env}',
  'config.tabHint': 'Tab indents. Press Esc then Tab to leave the editor.',
  'config.pushed': 'Pushed as v{version}. Your app picks it up within 5 seconds.',
  'config.noChanges': 'No changes — still v{version}.',
  'config.invalidJson': 'Invalid JSON',
  'config.invalidJsonLine': 'Invalid JSON on line {line}',
  'config.goToLine': 'Go to line',
  'config.issues': { one: '{count} validation issue — nothing was pushed', other: '{count} validation issues — nothing was pushed' },
  'config.root': '(root)',
  'config.lineShort': 'L{line}',
  'config.liveNote': 'Live version {version}. Every push is kept — to roll back, push an older file again.',
  'config.prodTitle': 'Push to your published app?',
  'config.prodConfirm': 'Push to your published app',
  'config.prodBody': 'Live users get the new definitions within seconds — prices and rewards included.',

  // ------------------------------------------------------------ leaderboards / achievements
  'lb.title': 'Leaderboards & achievements',
  'lb.noBoardsTitle': 'No leaderboards defined',
  'lb.noBoardsBody': 'Add a stat and a leaderboard to {file}, then {cmd} — or edit it in {link}.',
  'lb.configLink': 'Config',
  'lb.tabs': 'Leaderboards',
  'lb.stat': 'stat: {stat}',
  'lb.lowestWins': 'lowest wins',
  'lb.highestWins': 'highest wins',
  'lb.reset.daily': 'resets daily',
  'lb.reset.weekly': 'resets weekly',
  'lb.reset.monthly': 'resets monthly',
  'lb.reset.never': 'never resets',
  'lb.reset.other': 'resets {reset}',
  'lb.season': 'Season',
  'lb.thisSeason': 'This season',
  'lb.lastSeason': 'Last season',
  'lb.noScoresTitle': 'No scores yet',
  'lb.noScoresBody': 'Scores appear when users submit {call} in period {period}.',
  'lb.ranked': { one: '{count} ranked user · period {period}', other: '{count} ranked users · period {period}' },
  'lb.what': 'Leaderboard',
  'ach.title': 'Achievements',
  'ach.eligible': { one: '{count} eligible user', other: '{count} eligible users' },
  'ach.emptyTitle': 'No achievements defined',
  'ach.emptyBody': 'Achievements with a {condition} unlock automatically from stats; without one, your app calls {call}.',
  'ach.hidden': 'hidden',
  'ach.mission.daily': 'daily mission',
  'ach.mission.weekly': 'weekly mission',
  'ach.mission.monthly': 'monthly mission',
  'ach.mission.other': '{reset} mission',
  'ach.manual': 'manual unlock',
  'ach.meter': '{pct}% of users unlocked',
  'ach.unlockedBy': { one: '{n} user · {pct}%', other: '{n} users · {pct}%' },

  // ------------------------------------------------------------ multiplayer
  'mp.title': 'Multiplayer',
  'mp.live': 'live · refreshes every 5s',
  'mp.liveRooms': 'Live rooms',
  'mp.playersInRooms': 'Users in rooms',
  'mp.inMatchmaking': 'In matchmaking',
  'mp.rooms24h': 'Rooms · 24h',
  'mp.avg': 'avg {dur}',
  'mp.byMode': 'By mode',
  'mp.noRoomsTitle': 'No rooms open right now',
  'mp.noRoomsBody': 'Declare modes under {key} in your config, then call {call} from two browser tabs to see a room appear.',
  'mp.col.mode': 'Mode',
  'mp.col.rooms': 'Rooms',
  'mp.col.players': 'Users',
  'mp.col.queued': 'Queued',
  'mp.recent': 'Recently ended',
  'mp.noRecent': 'No finished rooms yet.',
  'mp.col.ended': 'Ended',
  'mp.col.length': 'Length',
  'mp.col.peak': 'Peak / total',
  'mp.col.how': 'How',
  'mp.private': 'private',

  // ------------------------------------------------------------ hosting
  'hosting.title': 'Hosting',
  'hosting.liveUrl': 'Live URL',
  'hosting.byDeveloper': 'by you (signed in)',
  'hosting.byKey': 'by a server-only key (sk_)',
  'hosting.noUrl': 'This server has no public URL for hosted apps yet.',
  'hosting.liveSince': 'live since {ago}',
  'hosting.files': { one: '{count} file', other: '{count} files' },
  'hosting.nothingLive': 'Nothing live yet',
  'hosting.emptyTitle': 'Nothing published yet',
  'hosting.deployments': 'Publish history',
  'hosting.livePill': 'live',
  'hosting.noMessage': 'No message',
  'hosting.rollBack': 'Go back to this one',
  'hosting.makeLive': 'Make live',
  'hosting.nowLive': 'v{version} is now live.',
  'hosting.rollbackUnavailable': 'Rollback is not available on this server yet.',
  'hosting.confirmTitle': 'Make v{version} live in {env}?',
  'hosting.confirmLabel': 'Switch to v{version}',
  'hosting.confirmBody': 'Users loading the app from now on get v{version}. The switch is instant and you can switch back at any time.',
  // 独自ドメイン（components/HostingDomains.tsx・2026-09-27 creator/custom-domains）
  'domains.title': 'Custom domains',
  'domains.lead': 'Serve the app on a domain you own, such as app.example.com. Users, share links and X cards then use it and never see a {product} URL. {product} handles the certificate; you add the DNS records.',
  'domains.devNote': 'Custom domains are for your published app. Your test app keeps its own URL. To add one, choose Published under “App you are working on”.',
  'domains.none': 'No custom domains yet.',
  'domains.hostname': 'Domain',
  'domains.placeholder': 'app.example.com',
  'domains.add': 'Add domain',
  'domains.added': '{hostname} was added. Add both DNS records below (CNAME and TXT) where you manage the domain, then press Check now.',
  'domains.status.pending': 'waiting for DNS',
  'domains.status.active': 'live',
  'domains.status.failed': 'failed',
  'domains.dnsLead': 'Add both records at the same time where you manage the domain (your registrar or DNS provider): the CNAME points the name at {product}, and the TXT proves the domain is yours. {product} checks every hour and starts serving once they are found (minutes to a few hours). Watch the status and the ownership badge.',
  // 持ち主の証明（統合⑰・SEC-R3-07）。TXT は active の後も毎日確かめ直す = 消すと配信が止まる
  'domains.verified': 'ownership verified',
  'domains.unverified': 'ownership not verified yet',
  'domains.verifiedAt': 'Ownership TXT last confirmed {at}.',
  'domains.dnsKeep': 'DNS records (keep them in place)',
  'domains.txtKeep': 'Keep the TXT record after the domain is live: {product} re-checks it every day, and stops serving on the domain if it is gone.',
  'domains.parentTxt': 'One TXT record on the parent domain ({name}) covers every name under it, so other subdomains need no TXT of their own.',
  'domains.dnsLabel': 'DNS records',
  'domains.apexNote': 'This is a root domain: some DNS providers cannot put a CNAME there. Use ALIAS / ANAME / CNAME flattening, or a subdomain such as play.<your domain>.',
  'domains.check': 'Check now',
  'domains.checked': 'Checked {hostname}: {status}.',
  'domains.open': 'Open',
  'domains.remove': 'Remove',
  // 2026-10-06 creator/domain-words: 以前の「日割りで原価」の英文はドメインの代金を取るように読めた。{price} はサーバーの pricing（hostingDomains.ts の domainCostMessage）
  'domains.cost': 'Using your domain on {product} costs {price} a month per domain (the certificate and serving on it), taken from your prepaid balance by the day. You still pay for the domain itself where you bought it. Remove the ones you do not use.',
  'domains.costUnpriced': 'Using your domain on {product} costs a monthly amount per domain (the certificate and serving on it), taken from your prepaid balance by the day. You still pay for the domain itself where you bought it. Remove the ones you do not use.',
  'domains.cli': 'From a terminal: {cmd}',
  'domains.removeTitle': 'Remove {hostname}?',
  'domains.removeBody': 'The app stops being served on {hostname} right away, and links to it stop working. It stays on its {product} URL. The domain can then be taken by someone else.',
  'domains.removeType': 'Type {hostname} to confirm.',
  'domains.removeLabel': 'Remove domain',
  'domains.removed': '{hostname} was removed.',
  // パスごとのアプリ（2026-10-06 compute/domain-paths）: 1 つのドメインの下にパスごとに別のアプリ。並べられるのは同じアカウントのアプリだけ
  'domains.path': 'Path (optional)',
  'domains.pathPlaceholder': '/race',
  'domains.pathHint': 'Leave empty to use the whole domain. Your apps can share one domain, each at its own path (example.com/race, example.com/dash). In an app under a path, write file and link paths without a leading "/" (images/a.png, not /images/a.png).',
  'domains.willShow': 'The app will be at {url}',
  'domains.addedLive': '{hostname} was added and is live at {url}. Another of your apps already uses this domain, so no DNS records are needed.',
  'domains.appUrl': 'This app: {url}',
  'domains.otherPaths': 'This domain also shows: {paths}',
  'domains.warningsTitle': 'These parts of the app may not work at {hostname}. {product} does not change your files:',
  'domains.removeBodyPath': 'This app stops being served at {url} right away, and links to it stop working. It stays on its {product} URL. The domain stays for your other apps on it ({paths}).',
  'domains.removeLabelPath': 'Remove this app from the domain',
  'deploy.status.pending': 'pending',
  'deploy.status.uploading': 'uploading',
  'deploy.status.ready': 'ready',
  'deploy.status.failed': 'failed',

  // ------------------------------------------------------------ payments
  'pay.title': 'Payments',
  'pay.notConfigured': 'not configured',
  'pay.mode.fake': 'fake checkout',
  'pay.mode.test': 'Stripe test mode',
  'pay.mode.live': 'Stripe live',
  'pay.noSalesTitle': 'No {env} sales yet',
  'pay.noSalesBody':
    'Declare {products} in your {link}, then call {call} from a button in your app. Paid purchases show up here as soon as the payment is confirmed.',
  'pay.configLink': 'config',
  'pay.perCurrency': 'Totals are per currency and never converted.',
  'pay.currency': 'Currency',
  'pay.revenueIn': 'Revenue in {currency}',
  'pay.gross': 'Gross sales',
  'pay.paidPurchases': { one: '{count} paid purchase', other: '{count} paid purchases' },
  'pay.refunds': 'Refunds',
  'pay.inclDisputed': 'incl. {amount} disputed',
  'pay.refundsHint': 'refunds and chargebacks',
  'pay.fee': '{product} fee',
  'pay.feeHint': 'estimate · {pct} of net sales',
  'pay.net': 'Net to you',
  'pay.netHint': "estimate · before Stripe's processing fees",
  'pay.daily': 'Daily sales',
  'pay.dailyRange': 'Last 30 days · UTC · gross {currency}',
  'pay.dailyLabel': 'Daily gross sales in {currency}, last 30 days',
  'pay.notice.noneTitle': "Payments aren't configured on this server.",
  'pay.notice.noneBody': 'Checkout answers {code} until the server has {keyVar} and its webhook secret. Ask whoever runs your {product} server to set them.',
  'pay.notice.fakeTitle': 'Built-in fake checkout: no money moves.',
  'pay.notice.fakeBody':
    "This server has no Stripe key for {env}, so users get {product}'s Pay / Cancel test page. Grants, refunds and these numbers behave exactly as they will with Stripe. Set {keyVar} on the server to switch.",
  'pay.notice.testTitle': 'Stripe test mode: no real charges.',
  'pay.notice.testBody': "Pay with Stripe's test cards (e.g. {card}, any future date and CVC). Production uses live mode and your connected payout account.",
  'pay.liveBlockedTitle': 'Live purchases are blocked until onboarding is complete.',
  'pay.liveBlockedBody': 'Checkout answers {code}, so hide your store or show “coming soon” until then.',
  'pay.recent': 'Recent purchases',
  'pay.recentRange': 'Latest 50 · all statuses',
  'pay.noCheckouts': 'No checkouts started in {env} yet.',
  'pay.col.purchase': 'Purchase',
  'pay.col.player': 'User',
  'pay.col.amount': 'Amount',
  'pay.col.status': 'Status',
  'pay.col.when': 'When',
  'pay.col.actions': 'Actions',
  'pay.openPlayer': 'Open user',
  'pay.deletedPlayer': 'Deleted user',
  'pay.status.completed': 'paid',
  'pay.status.pending': 'pending',
  'pay.status.refunded': 'refunded',
  'pay.status.disputed': 'disputed',
  'pay.status.failed': 'failed',
  'pay.status.expired': 'expired',
  'pay.status.canceled': 'canceled',
  'pay.refund': 'Refund',
  'pay.refundTitle': 'Refund {amount}?',
  'pay.refundConfirm': 'Refund in full',
  'pay.boughtBy': '{title} bought by {who} {ago}.',
  'pay.refundLive': 'The full amount goes back to their card through Stripe, and your payout for it is reversed. {product} takes no fee, so there is none to return.',
  'pay.refundTest': 'Refunded in Stripe test mode. No real money moves.',
  'pay.refundFake': 'This is a fake-checkout purchase. No money moves.',
  'pay.refundGrants': "Everything the purchase granted is removed from the user (currency they already spent stops at 0). This can't be undone.",
  'pay.typeToConfirm': 'Type {key} to confirm',
  'pay.refunded': 'Refunded {amount} for {title}.',
  'pay.removed': 'Removed {items} from {who}.',
  'pay.nothingRemoved': 'Nothing left to remove from {who}.',
  'pay.listSep': ', ',

  // ------------------------------------------------------------ audit
  'audit.title': 'Activity log',
  'audit.filter': 'App',
  'audit.all': 'All',
  'audit.current': '{env} (current)',
  'audit.emptyTitle': 'Nothing recorded yet',
  'audit.emptyBody': 'Setting changes, key changes, publishes and bans show up here.',
  'audit.loadOlder': 'Load older',
  'audit.secretKey': 'server-only key {id}',
  // 2026-10-21 Core project handoff 0: 他の開発者の操作はメールを出さない（サーバーが actorRole だけ返す）
  'audit.previousOwner': 'Previous owner',
  // 言葉（2026-10-21 core/integ-fixes-1021）: 「developer」は平易な言葉の決まり（plainWords.test.ts）に当たる。同じ画面の audit.domainReleased の
  //   「another account」に合わせる。キーの名前（otherDeveloper）はサーバーの actorRole 'other_developer' と対にしたまま（変えると AuditPage と食い違う）
  'audit.otherDeveloper': 'Another account',
  'audit.projectCreate': 'Created project {slug}',
  'audit.projectDelete': 'Deleted project',
  'audit.keyCreate': 'Created a key: {kind}',
  'audit.keyRevoke': 'Turned off a key',
  'audit.configPush': 'Pushed config v{version}',
  'audit.playerBan': 'Banned user {id}',
  'audit.playerBanReason': 'Banned user {id} — “{reason}”',
  'audit.deploymentCreate': 'Uploaded v{version}',
  'audit.deploymentFinalize': 'v{version} ready',
  'audit.deploymentActivate': 'Made v{version} live',
  'audit.deploymentRollback': 'Went back to v{version} (from v{previous})',
  'audit.paymentsRefund': 'Refunded {amount}',
  'audit.paymentsConnect': 'Connected a payout account',
  'audit.playerUnban': 'Unbanned user {id}',
  // 2026-10-05 console/followups: サーバーが書く種類を全部（auditWords.ts の表・auditWords.test.ts がサーバーのコードと突き合わせる）
  'audit.projectRename': 'Renamed the project to “{name}”',
  // プロジェクトの受け渡し（2026-10-21 core/handoff-1a・auditWords.ts）
  'audit.transferInvited': 'Invited {email} to take over the project',
  'audit.transferCancelled': 'Cancelled the handover invitation',
  'audit.transferDeclined': 'The invited person declined the handover',
  'audit.transferAccepted': 'The project was handed over to a new owner',
  // 共同作業者（2026-10-22 core/handoff-1b・サーバーの actorRole owner / collaborator・action project.member.*）
  'audit.owner': 'Owner',
  'audit.collaborator': 'Collaborator',
  'audit.memberInvited': 'Added {email} as a collaborator',
  'audit.memberRemoved': 'Removed collaborator {email}',
  'audit.memberLeft': 'A collaborator stopped helping',
  'audit.slugChange': 'Changed the name in your web address from {from} to {to}',
  'audit.slugRedirectOn': 'The old address name {slug} now forwards here',
  'audit.slugRedirectOff': 'The old address name {slug} no longer forwards here',
  'audit.keyRotateAll': 'Replaced all server-only keys',
  'audit.authProviderUpdate': 'Changed sign-in with {provider}',
  'audit.authProviderDelete': 'Removed sign-in with {provider}',
  'audit.playerDelete': 'Deleted the data of user {id}',
  'audit.playerExport': 'Downloaded the data of user {id}',
  'audit.playerAge': 'Set the age group of user {id}',
  'audit.playerSanction': 'Restricted user {id}',
  'audit.playerSanctionRevoke': 'Removed a restriction on user {id}',
  'audit.playerSanctionLift': 'A restriction on user {id} ended',
  'audit.appealAccepted': 'Accepted the appeal from user {id}',
  'audit.appealDeclined': 'Turned down the appeal from user {id}',
  'audit.paymentBlock': 'Stopped user {id} from paying',
  'audit.paymentUnblock': 'Let user {id} pay again',
  'audit.chatHide': 'Hid a message',
  'audit.chatUnhide': 'Showed a hidden message again',
  'audit.deploymentCancel': 'Cancelled the upload of v{version}',
  'audit.blobAdopt': 'Made {count} file(s) already in this project (Blender or AI 3D output) ready to publish',
  'audit.uploadSessionCreate': "Got ready to receive files from an AI's own computer",
  'audit.uploadSessionClaim': "Received v{version} from an AI's own computer",
  'audit.servingSuspend': 'Our team took the site offline',
  'audit.servingResume': 'Our team put the site back online',
  'audit.hostingPromote': 'Our team turned on faster file delivery for your published app',
  'audit.hostingDemotePause': 'Our team paused faster file delivery for your published app for now (your app still works as usual)',
  'audit.hostingDemote': 'Our team turned off faster file delivery for your published app (your app still works as usual)',
  'audit.hostingPromoteOffAll': 'Our team paused faster file delivery for all published apps at once, including this one (your app still works as usual)',
  'audit.functionsEnable': 'Turned on Functions',
  'audit.functionsDisable': 'Turned off Functions',
  'audit.functionsLimits': 'Changed the limits per call',
  'audit.functionsDeploy': 'Published server v{version}',
  'audit.functionsDelete': 'Deleted the server',
  'audit.functionsSuspend': 'The server was paused',
  'audit.functionsResume': 'The server is running again',
  'audit.secretPut': 'Saved the secret {name}',
  'audit.secretDelete': 'Removed the secret {name}',
  'audit.functionsSweep': 'Cleaned up the server after the project was deleted',
  'audit.domainAdd': 'Added the domain {host}',
  'audit.domainRemove': 'Removed the domain {host}',
  'audit.domainVerified': 'The domain {host} is now working',
  'audit.domainDeactivated': 'The domain {host} stopped working',
  'audit.domainReleased': 'The domain {host} was released because another account proved it owns it',
  'audit.shareLinkCreate': 'Made a share link',
  'audit.delegationRevoke': 'Disconnected an AI app',
  'audit.delegationReplaced': 'Disconnected the older connection of an AI app because it was connected again',
  'audit.storeCredentialsPut': 'Saved the {store} connection details',
  'audit.storeCredentialsDelete': 'Removed the {store} connection details',
  'audit.storeNoticesPut': 'Saved the Google Play notification settings',
  'audit.storeNoticesDelete': 'Removed the Google Play notification settings',
  'audit.giftCreate': 'Sent items to users',
  'audit.giftCancel': 'Called off sending items to users',
  'audit.serialCreate': 'Made {count} redeem codes',
  'audit.serialDisable': 'Turned off a set of redeem codes',
  'audit.mailSender': 'Changed the email sender settings',
  'audit.mailCreate': 'Drafted an email to users',
  'audit.mailSend': 'Sent an email to {count} users',
  'audit.mailCancel': 'Called off an email to users',
  'audit.voiceSuspend': 'Our team paused voice in the room {room}',
  'audit.voiceResume': 'Our team turned voice back on in the room {room}',
  'audit.contentPurge': 'Our team removed reported content',
  'audit.reportResolve': 'Our team finished handling a report from a user',
  'audit.adsSuspend': 'Our team paused ads',
  'audit.adsResume': 'Our team turned ads back on',
  'audit.adsTxt': 'Updated the ads.txt file',
  'audit.adNetworkLink': 'Joined the ad network',
  'audit.adNetworkUnlink': 'Left the ad network',
  'audit.adsImport': 'Imported ad earnings',

  // ------------------------------------------------------------ identity（0013: 復旧・メール確認・サインイン方法）
  'auth.forgotLink': 'Forgot password?',
  'recover.forgotTitle': 'Reset your password',
  'recover.forgotLead': "Enter your account email. If there's an account, we'll send a link to choose a new password.",
  'recover.send': 'Send reset link',
  'recover.sent': 'If an account exists for {email}, a reset link is on its way. It expires in 30 minutes.',
  'recover.devOutbox': 'Running {product} locally? Mail is not sent — open {link} to find the link.',
  'recover.backToLogin': 'Back to log in',
  'recover.requestNew': 'Request a new link',
  'recover.resetTitle': 'Choose a new password',
  'recover.resetLead': 'Just in case, saving signs you out of the dashboard and terminal commands on every device. Then log in again with your new password.',
  'recover.newPassword': 'New password',
  'recover.account': 'Account',
  'recover.resetFor': 'New password for {email}',
  'recover.confirmPassword': 'Repeat new password',
  'recover.mismatch': "The two passwords don't match.",
  'recover.save': 'Save new password',
  'recover.resetDone': 'Password changed. Log in with your new password.',
  'recover.missingToken': 'This link is incomplete. Open the link from the email again, or request a new one.',
  // /verify-email の見出し（済む前 = ボタンを押す前・確かめている間・失敗）。済んだ後は verify.doneTitle（en.verify.ts）。
  // 2026-11-05 core/first5: 以前は「Check your email address」= 「受信箱を見て」と読めた（11/4 の通し findings.md の 4）→ 頼む形に
  'recover.verifyTitle': 'Confirm your email address',
  'recover.verifying': 'Confirming…',
  // 済んだ後の本文。見出し（Email confirmed）と同じことを繰り返さず、確認で開いた物 = 次にできることを言う（emailUnverified.notice で止めていた物）
  'recover.verified': 'You can now connect your AI and add funds.',
  'recover.continue': 'Continue to the dashboard',
  // 2026-09-28 SEC-DEVLOGIN-01: 確認が済むまで sk_・MCP の接続・ログイン方法の追加・出金の設定が使えない = 「復旧のため」だけでは足りない
  'verifyBanner.text': 'We have not yet checked that the email address {email} is yours. Open the link in the email we sent you. Until you do, you cannot make server-only keys (sk_), connect an AI agent, add sign-in methods, set up payouts (payout account, passkeys), add prepaid funds or turn on automatic top-up.',
  // 出金の設定（payouts）を見せない間の同じ文（2026-09-29 creator/launch-money）
  'verifyBanner.textLaunch': 'We have not yet checked that the email address {email} is yours. Open the link in the email we sent you. Until you do, you cannot make server-only keys (sk_), connect an AI agent, add sign-in methods, add prepaid funds or turn on automatic top-up.',
  'verifyBanner.resend': 'Send the email again',
  'verifyBanner.sent': 'Sent — check your inbox (and your spam folder if you cannot find it).',
  'error.invalid_token': 'This link cannot be used (it expired or was already used). Ask for a new one.',
  'error.redirect_not_allowed': 'That URL is not in the allowed return URLs.',
  'error.secrets_not_configured': 'Settings for sign-in with Google and others cannot be saved yet. Please contact support.',
  'error.provider_secret_unreadable': 'The saved sign-in settings (the values you got from Google or another site) can no longer be read. Please enter them again.',

  'nav.signIn': 'Sign-in methods',
  'signin.title': 'Sign-in methods',
  'signin.lead':
    'Let users sign in with Google, Discord, Apple or X, and choose where sign-in and email links may return. Make separate apps on Google and the others for your test app and your published app.',
  'signin.secretsMissing': 'This server is not set up to store OAuth client secrets yet. Ask whoever runs the server to set it up.',
  'signin.redirectTitle': 'Allowed return URLs',
  'signin.redirectHelp':
    'One URL per line. An exact URL matches with any query string; end with * to allow every path below it. Used when users come back from Google / Discord / Apple and by links in verification and password-reset emails.',
  'signin.redirectAuto.development': 'Allowed here without listing them: localhost and 127.0.0.1 on any port, and this project on {product} hosting.',
  'signin.redirectAuto.production': 'Allowed without listing it: this project on {product} hosting. localhost is never allowed in your published app.',
  'signin.redirectPlaceholder': 'https://app.example.com/*',
  'signin.saveUrls': 'Save URLs',
  'signin.saved': 'Saved.',
  'signin.enabled': 'On',
  'signin.disabled': 'Off',
  'signin.configured': 'Credentials saved',
  'signin.notConfigured': 'No credentials',
  'signin.enable': 'Let users sign in with {provider}',
  // KUMODeck でログイン（2026-10-07 core/kumodeck-signin）。鍵なし = on にするだけ
  'signin.deck.title': '{product}',
  'signin.deck.enable': 'Let users sign in with {product}',
  'signin.deck.help': 'Nothing to set up: no app to register and no keys. Users sign in with one {product} account (email link, Google, Discord or X) that works across apps. Your app gets an ID made just for it and the user\'s display name, never their email.',
  'signin.enableNeedsCreds': 'Save the credentials below — until then, sign-in attempts are refused.',
  'signin.help.google': 'In Google Cloud Console → APIs & Services → Credentials, create an OAuth client ID of type "Web application".',
  'signin.help.discord': 'In the Discord Developer Portal, create an application and open OAuth2.',
  'signin.help.apple': 'In Apple Developer → Certificates, IDs & Profiles, create a Services ID and a key with Sign in with Apple enabled.',
  'signin.help.x': 'In the X Developer Portal, create your own app (type: Web App, OAuth 2.0 with PKCE), add the redirect URI below, and paste its OAuth 2.0 Client ID and Client Secret. Don\'t put "X" or "Twitter" in the app name or icon. Users see your app\'s name on X\'s consent screen; X bills its API use to you directly.',
  'signin.x.choice': 'Which X app to use',
  'signin.x.shared': 'Use without setup (no X fees)',
  'signin.x.own': 'Use my own X app',
  'signin.x.sharedHelp': 'No X app needed, and X does not charge for sign-in ({product} usage is still billed at cost from your prepaid balance). The consent screen shows a neutral shared app name, not your app\'s.',
  'signin.x.sharedSwitch': 'To switch to the shared app, remove your saved X credentials.',
  'signin.x.removeBodyShared': 'Users in {env} will sign in with X through the shared app instead of yours. Their accounts and progress are kept.',
  'signin.callback': 'Redirect URI to register with {provider}',
  'signin.clientId': 'Client ID',
  'signin.servicesId': 'Services ID',
  'signin.clientSecret': 'Client secret',
  'signin.secretKeep': 'Leave empty to keep the saved secret ({hint}).',
  'signin.teamId': 'Team ID',
  'signin.keyId': 'Key ID',
  'signin.privateKey': 'Private key (.p8 file contents)',
  'signin.privateKeyKeep': 'Leave empty to keep the saved key.',
  'signin.saveCreds': 'Save credentials',
  'signin.remove': 'Remove credentials',
  'signin.removeTitle': 'Remove the {provider} credentials?',
  'signin.removeBody': "Users can't sign in with {provider} in {env} until you add credentials again. Their accounts and progress are kept.",
  'signin.sdkTitle': 'In your app',
  'signin.sdkComment': 'call from a click handler (opens a popup)',

  // 使えるお金が無い（canSpend が false）ときの帯（components/PrepaidBand.tsx・2026-09-27 prepaid-onboarding → canspend で言い切る形に）
  'prepaidBand.start': 'Add prepaid credit to start. There is no free allowance: what you use is taken from this balance at cost (no markup). Until you add funds, your published apps, servers and multiplayer stay stopped for the people using them, and you cannot create or publish projects.',
  'prepaidBand.owed': 'Your projects are paused for their users (published apps, servers, multiplayer) and you cannot create or publish projects: your prepaid balance is used up ({owed} owed) and neither earnings nor automatic top-up cover it. Everything resumes on its own once you add funds.',
  'prepaidBand.empty': 'Your projects are paused for their users (published apps, servers, multiplayer) and you cannot create or publish projects: your prepaid balance is used up and neither earnings nor automatic top-up cover it. Everything resumes on its own once you add funds.',
  // 売上（payouts）を見せない間の同じ文（「売上からの充当」を言わない・2026-09-29 creator/launch-money）
  'prepaidBand.owedLaunch': 'Your published apps, servers and multiplayer are stopped for the people using them, and you cannot create or publish projects: your prepaid balance ran out ({owed} short) and automatic top-up is off or failed. Everything starts again on its own once you add funds.',
  'prepaidBand.emptyLaunch': 'Your published apps, servers and multiplayer are stopped for the people using them, and you cannot create or publish projects: your prepaid balance ran out and automatic top-up is off or failed. Everything starts again on its own once you add funds.',
  'prepaidBand.startUnverified': 'First, open the link in the email we sent you (see above) to show the address is yours. Then add prepaid credit to create and publish projects. There is no free allowance.',
  // 招待の確認待ち・残高が少ない（2026-09-29 creator/launch-close-2・Money の前払いの道 16）。止まっていない = 「止まっている」と言わない
  'prepaidBand.invitePending': 'Open the link in the email we sent you: once you do, {amount} of invite credit is added and you can start without adding funds.',
  'prepaidBand.low': 'Your prepaid balance is running low ({balance} left). Your apps stop when it runs out, so add funds.',
  'prepaidBand.lowInvite': 'Your invite credit is almost used up ({balance} left). To keep going, add prepaid funds.',
  'prepaidBand.link': 'Add funds'
} satisfies Record<string, string | { one: string; other: string }>;
